import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UserProfile } from '../store/user-profile/user-profile.model';
import { Store } from '@ngrx/store';
import * as fromUserProfile from 'src/app/modules/core/store/user-profile';
import { map, take } from 'rxjs/operators';
import moment from 'moment';
import { DEFAULT_APP_CULTURE_CODE } from '../languages/language.model';

@Injectable({
  providedIn: 'root'
})
export class CultureService {
  constructor(private store: Store<any>, private translateService: TranslateService) {}

  public getTranslateService(): TranslateService {
    return this.translateService;
  }

  public get currentLang(): string {
    return this.translateService.currentLang;
  }

  public getUserProfile(): UserProfile {
    let userProfile: UserProfile = null;

    this.store
      .select(fromUserProfile.selectProfileUser)
      .pipe(
        take(1),
        map(value => (userProfile = value))
      )
      .subscribe();

    return userProfile;
  }

  public getDateDisplay(value: any, format: string): string {
    const userProfile = this.getUserProfile();

    let timeFormat = this.translateService.instant(`FORMAT.${format}`);

    if (userProfile?.culture?.use24HTime === true) {
      timeFormat = timeFormat.replace('hh', 'HH');
      timeFormat = timeFormat.replace(' A', '');
    }

    moment.locale(this.translateService.currentLang ?? DEFAULT_APP_CULTURE_CODE);
    const dt = moment(value);

    return dt.format(timeFormat);
  }

  public getNumberDisplay(value: any, format: string): string {
    if (format === 'NUMBER') {
      return this.getSimpleNumberDisplay(value);
    }
    if (format === 'NUMBER_WHOLE') {
      return this.getWholeNumberDisplay(value);
    }
    return format === 'NUMBER_ACCOUNTING' ? this.getCurrencyDisplay(value) : this.getDecimalDisplay(value);
  }

  public getDecimalDisplay(value: any): string {
    const style = 'decimal';

    return Intl.NumberFormat(this.translateService.currentLang ?? DEFAULT_APP_CULTURE_CODE, {
      useGrouping: false,
      minimumFractionDigits: 2,
      style: style
    }).format(value);
  }

  public getWholeNumberDisplay(value: any): string {
    return Intl.NumberFormat(this.translateService.currentLang ?? DEFAULT_APP_CULTURE_CODE, {
      useGrouping: true,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }

  public getSimpleNumberDisplay(value: any): string {
    return Intl.NumberFormat(this.translateService.currentLang ?? DEFAULT_APP_CULTURE_CODE, {
      useGrouping: false,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }

  public getCurrencyDisplay(value: any, numberOfDigits: number = 2): string {
    return Intl.NumberFormat(this.translateService.currentLang ?? DEFAULT_APP_CULTURE_CODE, {
      minimumFractionDigits: numberOfDigits,
      maximumFractionDigits: numberOfDigits
    }).format(value);
  }

  public localeParseFloat(val: string): any {
    const userProfile = this.getUserProfile();
    const thousandsSeparator = userProfile?.culture?.thousandsSeparator ?? ',';
    const decimalSeparator = userProfile?.culture?.decimalSeparator ?? '.';
    let ret = this.replaceAll(val.trim(), thousandsSeparator, '');
    ret = this.replaceAll(ret, decimalSeparator, '.');
    return parseFloat(ret);
  }

  private replaceAll(val, search, replacement): string {
    return val.split(search).join(replacement);
  }
}
